import type { CompanyType } from "@entrilia/shared/api/clientTypes";
import {
  combineValidators,
  definedValidator,
  regexValidator,
  requiredValidator,
  websiteUrlValidator,
} from "@entrilia/shared/utilities/validators";
import type {
  BcCompany,
  BcTenantOption,
  BcTenantSelection,
  CreateFundManagerClientRequest,
  OrganizationInfo,
} from "../../../api/clientsManagementTypes";
import type { FormValidationResults } from "../../../utilities/forms";

export interface FundManagerProvisioningForm {
  organization: OrganizationInfo | null;
  title: string;
  clientCode: string;
  companyType: CompanyType;
  bcTenant: BcTenantOption | null;
  bcCompany: BcCompany | null;
  enableInvestorPortal: boolean;
  enableExpenseManagement: boolean;
  enablePortfolioMonitoringDataCollection: boolean;
  websiteUrl: string;
  continueIfClientAlreadyExists: boolean;
  validationResults: FormValidationResults<FormValidationKey>;
}

type FormValidationKey = "organization" | "title" | "clientCode" | "bcCompany" | "websiteUrl";

export const getInitialFundManagerProvisioningForm = (): FundManagerProvisioningForm => ({
  organization: null,
  title: "",
  clientCode: "",
  companyType: "Production",
  bcTenant: null,
  bcCompany: null,
  enableInvestorPortal: false,
  enableExpenseManagement: false,
  enablePortfolioMonitoringDataCollection: false,
  websiteUrl: "",
  continueIfClientAlreadyExists: false,
  validationResults: {},
});

const clientCodeValidator = combineValidators(
  requiredValidator,
  regexValidator(/^[a-z0-9]+$/, "Only lowercase letters and numbers are allowed")
);

export const validateFundManagerProvisioningForm = (
  form: FundManagerProvisioningForm
): FormValidationResults<FormValidationKey> => {
  return {
    organization: definedValidator("Required")(form.organization),
    title: requiredValidator(form.title),
    clientCode: clientCodeValidator(form.clientCode),
    bcCompany: definedValidator("Required")(form.bcCompany),
    websiteUrl: websiteUrlValidator()(form.websiteUrl),
  };
};

export const mapFundManagerProvisioningFormToRequest = (
  form: FundManagerProvisioningForm,
  bcTenant: BcTenantSelection
): CreateFundManagerClientRequest => ({
  continueIfClientAlreadyExists: form.continueIfClientAlreadyExists,
  organizationClientCode: form.organization?.clientCode ?? "",
  title: form.title.trim(),
  clientCode: form.clientCode.trim(),
  enableInvestorPortal: form.enableInvestorPortal,
  enableExpenseManagement: form.enableExpenseManagement,
  enablePortfolioMonitoringDataCollection: form.enablePortfolioMonitoringDataCollection,
  businessCentralApp: {
    companyId: form.bcCompany?.companyId ?? "",
    companyNameOverride: form.bcCompany?.name ?? "",
    tenantIdOverride: bcTenant.tenantId,
    environmentOverride: bcTenant.environment,
  },
  websiteUrl: form.websiteUrl.trim() || undefined,
  companyType: form.companyType,
});
